/**
 * 1x1 transparent PNG
 * @private
 * @type {string}
 * @memberOf woosmap.maps.utils
 */
const EMPTY_PNG =
    "data:image/png;base64," +
    "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

/**
 * @param {google.maps.Map|BMap.Map} map
 * @param {google.maps.ImageMapType|BMap.TileLayer} overlay
 * @param {Number} at
 * @memberOf woosmap.maps.utils
 */
function addImageOverlay(map, overlay, at) {
    at = at || 0;

    removeOverlayAt(map, overlay, at);

    map.overlayMapTypes.insertAt(at, overlay);
}

/**
 * @param {google.maps.Map|BMap.Map} map
 * @param {google.maps.ImageMapType|BMap.TileLayer} overlay
 * @param {Number} at
 * @memberOf woosmap.maps.utils
 */
function removeOverlayAt(map, overlay, at) {
    if (map.overlayMapTypes.getAt(at)) {
        map.overlayMapTypes.removeAt(at);
    }
}

/**
 * @private
 * @param {Number} updatedAt
 * @returns {string}
 * @memberOf woosmap.maps.utils
 */
function getTilesUrlTemplate(updatedAt) {
    let url;
    url = `/tiles/$Z-$X-$Y.png?key=${woosmap.public_key}`;

    if (updatedAt) {
        url += `&_=${encodeURIComponent(updatedAt)}`;
    }

    return woosmap.api_url + url;
}

/**
 *
 * @param {Number} updatedAt
 * @returns {string}
 * @memberOf woosmap.maps.utils
 */
function getJsonTilesUrlTemplate(updatedAt) {
    return getTilesUrlTemplate(updatedAt).replace(".png", ".json");
}

/**
 * @private
 * @param {Function} getTileUrl
 * @returns {google.maps.ImageMapType|BMap.TileLayer}
 * @memberOf woosmap.maps.utils
 */
function getImageOverlay(getTileUrl) {
    return new google.maps.ImageMapType({
        getTileUrl: getTileUrl,
        tileSize: new google.maps.Size(256, 256),
    });
}

/**
 * @private
 * @param {google.maps.Map|BMap.Map} map
 * @param cursor
 * @memberOf woosmap.maps.utils
 */
function setDraggableCursor(map, cursor) {
    map.setOptions({ draggableCursor: cursor });
}

/**
 *
 * @param {Object} options
 * @returns {google.maps.Marker|BMap.Marker}
 * @memberOf woosmap.maps.utils
 */
function getMarker(options) {
    const marker = new google.maps.Marker(options || {});
    setMarkerOptions(marker, options || {});
    return marker;
}

/**
 *
 * @param {google.maps.Marker|BMap.Marker} marker
 * @param {google.maps.MarkerOptions} options
 * @memberOf woosmap.maps.utils
 */
function setMarkerOptions(marker, options) {
    marker.setOptions(options);
}

/**
 * Sets the markers map.
 * @param {google.maps.Marker|BMap.Marker} marker
 * @param {google.maps.Map|BMap.Map} map
 * @memberOf woosmap.maps.utils
 */
function setMap(marker, map) {
    marker.setMap(map);
}

/**
 * sets the marker position
 * @param {google.maps.Marker|BMap.Marker} marker
 * @param {google.maps.MarkerOptions.position} position
 * @memberOf woosmap.maps.utils
 */
function setPosition(marker, position) {
    setMarkerOptions(marker, { position: position });
}

/**
 * Returns the map container
 * @param {google.maps.Map|BMap.Map} map
 * @returns {Element}
 * @memberOf woosmap.maps.utils
 */
function getMapDiv(map) {
    return map.getDiv();
}

/**
 * Allowed values for control position.
 * @enum {Number}
 * @memberOf woosmap.maps.utils
 */
const ControlPosition = {
    /**
     * Position control to the top left of the map.
     */
    TOP_LEFT: 0,
    /**
     * Position control to the top right of the map.
     */
    TOP_RIGHT: 1,
    /**
     * Position control to the very bottom left of the map (alongside google watermark).
     */
    BOTTOM_LEFT: 2,
    /**
     * Position control to the very bottom right of the map (alongside google watermark).
     */
    BOTTOM_RIGHT: 3,
    /**
     * Position control to the bottom left of the map.
     */
    LEFT_BOTTOM: 4,
    /**
     * Position control to the bottom right of the map.
     */
    RIGHT_BOTTOM: 5,
};

/**
 * Adds control to the map.
 * @param {google.maps.Map|BMap.Map} map
 * @param {Element} element
 * @param {woosmap.maps.utils.ControlPosition} anchor
 * @memberOf woosmap.maps.utils
 */
function addControl(map, element, anchor) {
    const controlPosition = [
        google.maps.ControlPosition.TOP_LEFT,
        google.maps.ControlPosition.TOP_RIGHT,
        google.maps.ControlPosition.BOTTOM_LEFT,
        google.maps.ControlPosition.BOTTOM_RIGHT,
        google.maps.ControlPosition.LEFT_BOTTOM,
        google.maps.ControlPosition.RIGHT_BOTTOM,
    ];
    map.controls[controlPosition[anchor]].push(element);
}

/**
 * Sets marker icon
 * @param {google.maps.Marker|BMap.Marker} marker
 * @param {google.maps.Icon|String} iconData
 * @memberOf woosmap.maps.utils
 */
function setIcon(marker, iconData) {
    marker.setOptions({ icon: iconData });
}

/**
 *
 * @param {Number} lat
 * @param {Number} lng
 * @returns {google.maps.LatLng|BMap.Point}
 * @memberOf woosmap.maps.utils
 */
function getLatLng(lat, lng) {
    return new google.maps.LatLng(lat, lng);
}

/**
 * @param {google.maps.Map|BMap.Map} map
 * @param {google.maps.LatLngBounds|BMap.Bounds} bounds
 */
function fitBounds(map, bounds) {
    const googleBounds = new google.maps.LatLngBounds(bounds.getSouthWest().toJSON(), bounds.getNorthEast().toJSON());
    map.fitBounds(googleBounds);
}

/**
 * @typedef {Object} createMapOptions
 * Represents the parameters for createMap.
 * @property {woosmap.maps.geometry.LatLng} [center] Zoom center.
 * @property {Number} [zoom]  If set and positive, set zoom level.
 * @property {boolean} [enableScrollWheelZoom] Enable scroll wheel zoom with Baidu.
 */

/**
 * @typedef {object} BaiduMapsOptions
 * Represents the parameters for Baidu map.
 * @property {BMap.MapStyle} mapStyle Baidu Map Style
 * @see {@link http://lbsyun.baidu.com/custom/list.htm|Baidu Map Styles}
 */

/**
 * Create a map object depending on the loaded library (Google or Baidu).
 *
 * @param {HTMLElement} element HTML element to attach map onto
 * @param {createMapOptions} options Map options.
 * @param {google.maps.MapOptions} [googleMapsOptions] Specific Google Maps options.
 * @param {BaiduMapsOptions} [baiduMapsOptions] Specific Baidu map options.
 * @returns {null|google.maps.Map|BMap.Map}
 * @memberOf woosmap.maps.utils
 */
function createMap(element, options, googleMapsOptions = {}, baiduMapsOptions = {}) {
    return new google.maps.Map(element, Object.assign(options, googleMapsOptions));
}

/**
 * Sets the center and zoom of a map.
 * @param {google.maps.Map|BMap.Map} map the map.
 * @param {google.maps.LatLng|BMap.Point} center the center to set on the map.
 * @param {Number} zoom the zoom level to set on the map.
 * @memberOf woosmap.maps.utils
 */
function centerAndZoom(map, center, zoom) {
    map.setZoom(zoom);
    map.panTo(center);
}

/**
 * Converts time data to readable time
 * @private
 * @param {Number|string} time time in seconds
 * @returns {String}
 */
function strftime(time) {
    const timeInt = Number.parseInt(time, 10);
    const hours = Math.floor(timeInt / 3600);
    const minutes = Math.round((timeInt - hours * 3600) / 60);

    let timeStr = "";

    if (hours > 0) {
        timeStr += `${hours.toString()} h`;
        if (minutes > 0) {
            timeStr += " ";
        }
    }

    if (minutes > 0) {
        timeStr += `${minutes.toString()} min`;
    }

    return timeStr;
}

/**
 * Sets map type (plan or satellite).
 * @param {google.maps.Map|BMap.Map} map
 * @param {String} type Could be "plan" or "satellite"
 */
function setMapType(map, type) {
    if (type === "satellite") {
        map.setMapTypeId(google.maps.MapTypeId.HYBRID);
    }

    if (type === "plan") {
        map.setMapTypeId(google.maps.MapTypeId.ROADMAP);
    }
}

/**
 * Adds event listener on polyline.
 * @param {BaiduPolyline|google.maps.Polyline} polyline
 * @param {String} event
 * @param {Function} callback
 */
function polylineAddListener(polyline, event, callback) {
    google.maps.event.addListener(polyline, event, callback);
}

/**
 * Removes event listener from polyline.
 * @param {BaiduPolyline|google.maps.Polyline} polyline
 * @param {String} event
 * @param {Function} callback
 */
function polylineRemoveListeners(polyline, event, callback) {
    google.maps.event.clearListeners(polyline, event);
}

/**
 * Converts distance data to readable distance
 * @private
 * @param {Number|string} dist distance in meters
 * @returns {String}
 */
function strfdist(dist) {
    const distance = Number.parseInt(dist);
    const km = distance / 1000;

    return km > 1 ? `${Math.round(km)} km` : `${distance.toString()} m`;
}

module.exports = {
    setDraggableCursor,
    getMarker,
    setIcon,
    setMap,
    setPosition,
    getMapDiv,
    setMarkerOptions,
    addControl,
    ControlPosition,
    getLatLng,
    fitBounds,
    overlays: {
        getImageOverlay,
        addImageOverlay,
        removeOverlayAt,
        getTilesUrlTemplate,
        getJsonTilesUrlTemplate,
    },
    EMPTY_PNG,
    createMap,
    centerAndZoom,
    strfdist,
    strftime,
    setMapType,
    polylineAddListener,
    polylineRemoveListeners,
};
